import type { SxProps, Theme } from '@mui/material'
import { tokens } from 'ComponentLibrary'
import type { ContentChipColor } from 'ComponentLibrary/src/piazza/content/ContentChip/ContentChip'

import type { KpiRow, ValXmlFieldAssignment } from '../types'
import { FORCED_DEFAULT_VALXML_FIELDS, MANUAL_ENTRY_CONFIDENCE, ROOT_DATE_VALXML_PATH } from '../constants'

type BooleanDisplayLabels = {
    true: string
    false: string
}

type ValXmlUserErrorCopy = {
    authExpired: string
    forbidden: string
    endpointMissing: string
    rateLimit: string
    server: string
    missingToken: string
    responseMissingJson: string
    responseInvalidJson: string
    responseInvalidFormat: string
    unknown: string
    genericAiError: (code?: number | string, bodyMessage?: string) => string
}

const DEFAULT_BOOLEAN_DISPLAY_LABELS: BooleanDisplayLabels = {
    true: 'Ja',
    false: 'Nein',
}

const DEFAULT_VAL_XML_USER_ERROR_COPY: ValXmlUserErrorCopy = {
    authExpired: 'Nicht angemeldet oder Sitzung abgelaufen. Bitte erneut anmelden.',
    forbidden: 'Zugriff verweigert. Dir fehlen Berechtigungen für die KI-Funktion.',
    endpointMissing: 'KI-Endpunkt nicht gefunden. Prüfe VITE_BACKEND_ROUTE und Backend-Verfügbarkeit.',
    rateLimit: 'Limit erreicht. Bitte später erneut versuchen.',
    server: 'Serverfehler bei der KI. Bitte später erneut versuchen.',
    missingToken: 'Nicht angemeldet. Bitte melde dich an und versuche es erneut.',
    responseMissingJson: 'Die KI-Antwort enthielt kein JSON.',
    responseInvalidJson: 'Die KI-Antwort war kein gültiges JSON.',
    responseInvalidFormat: 'Die Extraktionsantwort war nicht im erwarteten JSON-Format.',
    unknown: 'Unbekannter Fehler beim Abrufen der KI-Antwort.',
    genericAiError: (code, bodyMessage) =>
        bodyMessage ? `Fehler (${code ?? 'Unbekannt'}): ${bodyMessage}` : `Fehler (${code ?? 'Unbekannt'}) beim KI-Aufruf.`,
}

export const formatUnit = (unit?: string): string | undefined => {
    if (!unit) {
        return undefined
    }
    const trimmed = unit.trim()
    if (!trimmed) {
        return undefined
    }
    const normalizedEuro = trimmed.replace(/\bEUR\b/gi, '€')
    const lower = trimmed.toLowerCase()
    if (lower === 'stück' || lower === 'stueck' || lower === 'stk' || lower === 'st.') {
        return 'Stk.'
    }
    if (normalizedEuro.includes('€')) {
        return normalizedEuro
    }
    return trimmed
}

export type KpiValueFormat = 'text' | 'date' | 'decimal' | 'integer'

type KpiValueFormatOptions = {
    format?: KpiValueFormat
    locale?: string
    noGrouping?: boolean
}

export const formatDisplayValue = (
    value?: string | null,
    booleanLabels: BooleanDisplayLabels = DEFAULT_BOOLEAN_DISPLAY_LABELS,
    options: KpiValueFormatOptions = {}
): string => {
    if (typeof value !== 'string') {
        return ''
    }
    const lower = value.trim().toLowerCase()
    if (lower === 'true') {
        return booleanLabels.true
    }
    if (lower === 'false') {
        return booleanLabels.false
    }
    if (options.format === 'date') {
        return formatCanonicalDateForDisplay(value, options.locale) ?? value
    }
    if (options.format === 'decimal' || options.format === 'integer') {
        return formatCanonicalNumberForDisplay(value, options)
    }
    return value
}

export const normaliseInputValue = (
    input: string,
    booleanLabels: BooleanDisplayLabels = DEFAULT_BOOLEAN_DISPLAY_LABELS,
    options: KpiValueFormatOptions = {}
): string => {
    const trimmed = input.trim().toLowerCase()
    if (trimmed === booleanLabels.true.trim().toLowerCase() || trimmed === DEFAULT_BOOLEAN_DISPLAY_LABELS.true.toLowerCase()) {
        return 'true'
    }
    if (trimmed === booleanLabels.false.trim().toLowerCase() || trimmed === DEFAULT_BOOLEAN_DISPLAY_LABELS.false.toLowerCase()) {
        return 'false'
    }
    if (options.format === 'date') {
        return normaliseDateInput(input, options.locale) ?? input
    }
    if (options.format === 'decimal' || options.format === 'integer') {
        const normalized = normaliseNumberInput(input, options.locale)
        if (!normalized) {
            return input
        }
        if (options.format === 'integer' && normalized.includes('.')) {
            const integerValue = normalized.replace(/\.0+$/, '')
            return integerValue.includes('.') ? input : integerValue
        }
        return normalized
    }
    return input
}

const CANONICAL_NUMBER_PATTERN = /^[+-]?\d+(?:\.\d+)?$/
const ISO_DATE_PATTERN = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:T.*)?$/

const resolveDisplayLocale = (locale?: string): 'en-US' | 'de-DE' => (locale?.toLowerCase().includes('en') ? 'en-US' : 'de-DE')

const formatCanonicalNumberForDisplay = (value: string, options: KpiValueFormatOptions): string => {
    const trimmed = value.trim()
    if (!CANONICAL_NUMBER_PATTERN.test(trimmed)) {
        return value
    }
    const numericValue = Number(trimmed)
    if (!Number.isFinite(numericValue)) {
        return value
    }

    const fractionDigits = Math.min(trimmed.split('.')[1]?.length ?? 0, 20)
    return new Intl.NumberFormat(resolveDisplayLocale(options.locale), {
        useGrouping: !options.noGrouping,
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
    }).format(numericValue)
}

const formatCanonicalDateForDisplay = (value: string, locale?: string): string | null => {
    const isoDate = value.trim().match(ISO_DATE_PATTERN)
    if (!isoDate) {
        return null
    }
    const date = toValidUtcDate(isoDate[1], isoDate[2], isoDate[3])
    if (!date) {
        return null
    }
    return new Intl.DateTimeFormat(resolveDisplayLocale(locale), {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(date)
}

const normaliseDateInput = (input: string, locale?: string): string | null => {
    const trimmed = input.trim()
    if (!trimmed) {
        return null
    }

    const isoDate = trimmed.match(ISO_DATE_PATTERN)
    if (isoDate) {
        return toValidIsoDate(isoDate[1], isoDate[2], isoDate[3])
    }

    const localizedDate = trimmed.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{2}|\d{4})$/)
    if (!localizedDate) {
        return null
    }

    const isEnglish = resolveDisplayLocale(locale) === 'en-US'
    const month = isEnglish ? localizedDate[1] : localizedDate[2]
    const day = isEnglish ? localizedDate[2] : localizedDate[1]
    return toValidIsoDate(normalizeYear(localizedDate[3]), month, day)
}

const normalizeYear = (year: string): string => {
    if (year.length === 4) {
        return year
    }

    const numericYear = Number.parseInt(year, 10)
    return numericYear >= 70 ? `19${year}` : `20${year}`
}

const toValidUtcDate = (year: string, month: string, day: string): Date | null => {
    const numericYear = Number.parseInt(year, 10)
    const numericMonth = Number.parseInt(month, 10)
    const numericDay = Number.parseInt(day, 10)

    if (!Number.isInteger(numericYear) || !Number.isInteger(numericMonth) || !Number.isInteger(numericDay)) {
        return null
    }

    const date = new Date(Date.UTC(numericYear, numericMonth - 1, numericDay))
    if (date.getUTCFullYear() !== numericYear || date.getUTCMonth() !== numericMonth - 1 || date.getUTCDate() !== numericDay) {
        return null
    }
    return date
}

const toValidIsoDate = (year: string, month: string, day: string): string | null => {
    const date = toValidUtcDate(year, month, day)
    if (!date) {
        return null
    }
    return `${String(date.getUTCFullYear()).padStart(4, '0')}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(
        date.getUTCDate()
    ).padStart(2, '0')}`
}

const normaliseNumberInput = (input: string, locale?: string): string | null => {
    const trimmed = input.trim()
    if (!trimmed) {
        return null
    }

    let working = trimmed
        .replace(/\b(EUR)\b/gi, '')
        .replace(/\u2212/g, '-')
        .replace(/\s+/g, '')
    let sign = ''
    if (working.startsWith('+') || working.startsWith('-')) {
        sign = working[0] === '-' ? '-' : ''
        working = working.slice(1)
    }

    working = working.replace(/[^0-9,.-]/g, '')
    if (!working || !/\d/.test(working)) {
        return null
    }

    const displayLocale = resolveDisplayLocale(locale)
    const decimalSeparator = displayLocale === 'en-US' ? '.' : ','
    const thousandsSeparator = displayLocale === 'en-US' ? ',' : '.'
    let numeric = working

    if (working.includes(decimalSeparator)) {
        numeric = numeric.split(thousandsSeparator).join('')
        if (decimalSeparator === ',') {
            numeric = numeric.replace(',', '.')
        }
    } else if (isGroupedByThousands(working, thousandsSeparator)) {
        numeric = numeric.split(thousandsSeparator).join('')
    } else if (displayLocale === 'en-US' && working.includes(',')) {
        numeric = numeric.replace(',', '.')
    } else if (displayLocale === 'de-DE' && working.includes('.')) {
        numeric = isGroupedByThousands(working, '.') ? numeric.replace(/\./g, '') : numeric
    }

    if (!numeric || !/^\d+(?:\.\d+)?$/.test(numeric)) {
        return null
    }

    return sign ? `${sign}${numeric}` : numeric
}

const isGroupedByThousands = (value: string, separator: string): boolean => {
    const parts = value.split(separator)
    return parts.length > 1 && /^\d{1,3}$/.test(parts[0]) && parts.slice(1).every((part) => /^\d{3}$/.test(part))
}

export type ConfidenceStyle = {
    label: string
    background: string
    hover: string
    border: string
    chipColor: ContentChipColor
}

export const getConfidenceStyle = (score?: number, manualLabel = 'Manuell'): ConfidenceStyle | null => {
    if (typeof score !== 'number' || !Number.isFinite(score)) {
        return null
    }

    if (score === MANUAL_ENTRY_CONFIDENCE) {
        return {
            label: manualLabel,
            background: tokens.ColorSurfaceInformativeDefault,
            hover: tokens.ColorSurfaceInformativeInversePressed,
            border: tokens.ColorBorderInformative,
            chipColor: 'blue',
        }
    }

    const clamped = Math.min(Math.max(score, 0), 1)
    const percent = Math.round(clamped * 100)

    if (clamped < 0.5) {
        return {
            label: `${percent}%`,
            background: tokens.ColorSurfaceDangerDefault,
            hover: tokens.ColorSurfaceDangerInversePressed,
            border: tokens.ColorBorderDanger,
            chipColor: 'red',
        }
    }

    if (clamped < 0.9) {
        return {
            label: `${percent}%`,
            background: tokens.ColorSurfaceWarningDefault,
            hover: tokens.ColorSurfaceWarningInversePressed,
            border: tokens.ColorBorderWarning,
            chipColor: 'orange',
        }
    }

    return {
        label: `${percent}%`,
        background: tokens.ColorSurfacePositiveDefault,
        hover: tokens.ColorSurfacePositiveInversePressed,
        border: tokens.ColorBorderPositive,
        chipColor: 'green',
    }
}

type KpiRowStyleParams = {
    confidenceStyle: ConfidenceStyle | null
    hasClickHandler: boolean
}

export const getKpiRowStyles = ({ confidenceStyle, hasClickHandler }: KpiRowStyleParams): SxProps<Theme> => ({
    cursor: hasClickHandler ? 'pointer' : 'default',
    backgroundColor: confidenceStyle ? confidenceStyle.background : undefined,
    borderLeft: confidenceStyle ? `4px solid ${confidenceStyle.border}` : undefined,
    transition: 'background-color 120ms ease, transform 120ms ease',
    '&:hover': {
        backgroundColor: confidenceStyle ? confidenceStyle.hover : tokens.ColorSurfaceHover,
    },
    ...(hasClickHandler ? { '&:active': { transform: 'scale(0.995)' } } : {}),
})

export const normaliseExtractedKpis = (rows: KpiRow[]): KpiRow[] =>
    rows.map((kpi) => ({
        ...kpi,
        needsReview: kpi.needsReview ?? false,
        reviewNotes: kpi.reviewNotes,
        reviewDisagreed: kpi.reviewDisagreed ?? false,
        proposedValue: kpi.proposedValue ?? null,
        proposedMetadata: kpi.proposedMetadata ?? null,
    }))

export const mergeExtractedKpis = (currentRows: KpiRow[], nextRows: KpiRow[]): KpiRow[] =>
    normaliseExtractedKpis(dedupeKpiRows([...currentRows, ...nextRows]))

const FORCED_DEFAULT_PATH_SET = new Set<string>([...FORCED_DEFAULT_VALXML_FIELDS, ROOT_DATE_VALXML_PATH])

export const deriveValXmlAssignmentsFromKpis = (rows: KpiRow[]): ValXmlFieldAssignment[] => {
    const assignments: ValXmlFieldAssignment[] = []
    const seen = new Set<string>()

    rows.forEach((row) => {
        if (!row.valXmlPath) {
            return
        }
        const path = normaliseValXmlPath(row.valXmlPath)
        if (!path || FORCED_DEFAULT_PATH_SET.has(path) || seen.has(path)) {
            return
        }
        const rawValue = row.value?.trim()
        if (!rawValue || rawValue === '-' || rawValue === '—' || rawValue === '–') {
            return
        }
        assignments.push({
            path,
            value: rawValue,
            source: row.sourceText,
        })
        seen.add(path)
    })

    return assignments
}

export const toUserErrorMessage = (error: unknown, copy: ValXmlUserErrorCopy = DEFAULT_VAL_XML_USER_ERROR_COPY): string => {
    if (error instanceof Error && 'statusCode' in error) {
        const err = error as Error & { statusCode?: number; body?: { message?: unknown } }
        const code = err.statusCode
        const bodyMessage = typeof err.body?.message === 'string' ? err.body.message : undefined
        if (code === 401) {
            return copy.authExpired
        }
        if (code === 403) {
            return copy.forbidden
        }
        if (code === 404) {
            return copy.endpointMissing
        }
        if (code === 429) {
            return copy.rateLimit
        }
        if (code && code >= 500) {
            return copy.server
        }
        return copy.genericAiError(code, bodyMessage)
    }
    if (error instanceof Error && error.name === 'MissingTokenError') {
        return copy.missingToken
    }
    if (error instanceof Error) {
        return error.message || copy.unknown
    }
    return copy.unknown
}

const dedupeKpiRows = (rows: KpiRow[]): KpiRow[] => {
    const seenByPath = new Set<string>()
    const seenByName = new Set<string>()
    const result: KpiRow[] = []

    rows.forEach((row) => {
        const normalisedPath = row.valXmlPath ? normaliseValXmlPath(row.valXmlPath) : undefined
        if (normalisedPath && FORCED_DEFAULT_PATH_SET.has(normalisedPath)) {
            return
        }
        const nameKey = row.name.trim().toLowerCase()

        if (normalisedPath) {
            if (seenByPath.has(normalisedPath)) {
                return
            }
            seenByPath.add(normalisedPath)
        } else if (seenByName.has(nameKey)) {
            return
        } else {
            seenByName.add(nameKey)
        }

        if (normalisedPath && normalisedPath !== row.valXmlPath) {
            result.push({ ...row, valXmlPath: normalisedPath })
        } else {
            result.push(row)
        }
    })

    return result
}

const normaliseValXmlPath = (input: string): string | undefined => {
    if (!input) {
        return undefined
    }
    const trimmed = input.trim()
    if (!trimmed) {
        return undefined
    }

    const withoutNamespace = trimmed.replace(/(^|[./])ns1:/gi, '$1')
    const normalizedSeparators = withoutNamespace.replace(/[\\/]+/g, '.').replace(/\s+/g, '')
    const condensed = normalizedSeparators.replace(/\.+/g, '.').replace(/^\./, '').replace(/\.$/, '')
    if (!condensed) {
        return undefined
    }

    const segments = condensed.split('.').filter((segment) => segment.length > 0)
    if (segments.length === 0) {
        return undefined
    }

    if (segments[0].toLowerCase() !== 'valxml') {
        segments.unshift('ValXML')
    } else {
        segments[0] = 'ValXML'
    }

    return segments.join('.')
}
